import { type LocationDto, updateLocation, useLocations } from "@repo/data-store";
import { EmptyState } from "@repo/ui/components/empty-state";
import { Badge } from "@repo/ui/components/ui/badge";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Calendar, MapPin, Plus } from "lucide-react";
import { useMemo } from "react";
import { LOCATION_STATUS_PICKER_OPTIONS } from "@/features/catalog/lib/resource-status-options";
import { ActionButton } from "@/shared/common/action-button";
import { GroupedList, type GroupedListGroup } from "@/shared/common/grouped-list";
import { ResourceDisplayPath } from "@/shared/common/resource-display-path";
import { SearchFilterBar } from "@/shared/common/search-filter-bar";
import { formatDate } from "@/shared/lib/date-formats";
import { buildLocationTree } from "@/shared/lib/resource-utils";
import { StatusPickerBadge } from "@/shared/status/status-picker-badge";

export const Route = createFileRoute("/catalog/_layout/locations")({
  component: LocationsComponent,
  validateSearch: (search: Record<string, unknown>): { q?: string; status?: string } => ({
    q: (search.q as string) || undefined,
    status: (search.status as string) || undefined,
  }),
});

function LocationsComponent() {
  const { q: search = "", status: statusFilter } = Route.useSearch();
  const navigate = Route.useNavigate();
  const { permissions } = Route.useRouteContext();
  const setSearch = (value: string) =>
    navigate({ search: (prev) => ({ ...prev, q: value || undefined }), replace: true });
  const setStatusFilter = (value: string | null) =>
    navigate({ search: (prev) => ({ ...prev, status: value || undefined }), replace: true });
  const canCreateLocation = permissions.canWrite("Location");

  const { locations: allLocations, isLoading } = useLocations({
    ...(statusFilter ? { status: statusFilter as LocationDto["status"] } : {}),
  });

  const handleStatusChange = (location: LocationDto, status: string) => {
    updateLocation({
      id: location.locationId,
      status: status as "active" | "suspended" | "inactive",
    }).isPersisted.promise.catch(() => {});
  };

  // Locations read as a hierarchy, so the flat list is re-ordered parent-first.
  const orderedLocations = useMemo(() => {
    const lowered = search.toLowerCase();
    const matching = search ? allLocations.filter((l) => (l.name ?? "").toLowerCase().includes(lowered)) : allLocations;
    return buildLocationTree(matching).map((node) => node.location);
  }, [allLocations, search]);

  /** A Group is an organisational container, not a room; it reads muted. */
  const isGroup = (location: LocationDto) => location.physicalType?.text === "Group";

  const statusCell = (location: LocationDto) => (
    <StatusPickerBadge
      value={location.status ?? "unknown"}
      options={LOCATION_STATUS_PICKER_OPTIONS}
      onChange={(status) => handleStatusChange(location, status)}
    />
  );

  const typeBadge = (location: LocationDto) =>
    location.physicalType?.text ? (
      <Badge variant="outline" className="text-[10px] px-1.5 py-0 font-normal text-muted-foreground">
        {location.physicalType.text}
      </Badge>
    ) : null;

  const groups: GroupedListGroup<LocationDto>[] = [
    {
      id: "location",
      name: "Locations",
      items: orderedLocations,
      headerAction: canCreateLocation
        ? () => (
            <ActionButton state={{ allowed: true }} variant="ghost" size="icon-xs" to="/catalog/locations/new">
              <Plus className="size-4" />
            </ActionButton>
          )
        : undefined,
    },
  ];

  return (
    <GroupedList
      groups={groups}
      isLoading={isLoading}
      header={
        <SearchFilterBar
          search={search}
          onSearchChange={setSearch}
          placeholder="Search locations..."
          statusOptions={[
            { value: "active", label: "Active" },
            { value: "inactive", label: "Inactive" },
            { value: "suspended", label: "Suspended" },
          ]}
          statusValue={statusFilter ?? null}
          onStatusChange={setStatusFilter}
        />
      }
      columns={[
        { id: "resource", width: "1fr" },
        { id: "schedule", width: "auto" },
        { id: "date", width: "auto" },
        { id: "status", width: "100px" },
      ]}
      renderCells={(location) => [
        <div
          key="resource"
          className={`flex items-center gap-1.5 min-w-0 text-sm ${isGroup(location) ? "font-normal text-muted-foreground" : "font-medium"}`}
        >
          <ResourceDisplayPath path={location.displayPath} />
          {typeBadge(location)}
        </div>,
        location.scheduleCount > 0 ? (
          <span
            key="schedule"
            className="flex items-center justify-center text-xs text-muted-foreground"
            title="Has schedule"
          >
            <Calendar className="size-3.5" />
          </span>
        ) : null,
        location.updatedAt ? (
          <span key="date" className="text-xs text-muted-foreground text-right">
            {formatDate(location.updatedAt)}
          </span>
        ) : null,
        /* biome-ignore lint/a11y/noStaticElementInteractions: stop the picker from navigating the parent link */
        <div
          key="status"
          className="flex justify-end"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          {statusCell(location)}
        </div>,
      ]}
      wrapItem={(location, children) => (
        <Link
          to="/catalog/locations/$locationId"
          params={{ locationId: location.locationId }}
          className="cursor-pointer"
        >
          {children}
        </Link>
      )}
      renderItem={(location) => (
        <Link
          to="/catalog/locations/$locationId"
          params={{ locationId: location.locationId }}
          className="flex items-center justify-between px-4 py-3 hover:bg-muted/30 transition-colors cursor-pointer"
        >
          <div
            className={`flex items-center gap-1.5 min-w-0 text-sm ${isGroup(location) ? "font-normal text-muted-foreground" : "font-medium"}`}
          >
            <ResourceDisplayPath path={location.displayPath} />
            {typeBadge(location)}
          </div>
          {/* biome-ignore lint/a11y/noStaticElementInteractions: stop the picker from navigating the parent link */}
          <div
            className="flex items-center gap-4 shrink-0"
            onClick={(e) => e.preventDefault()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            {location.scheduleCount > 0 && (
              <span className="flex items-center text-xs text-muted-foreground" title="Has schedule">
                <Calendar className="size-3.5" />
              </span>
            )}
            {location.updatedAt && (
              <span className="text-xs text-muted-foreground">{formatDate(location.updatedAt)}</span>
            )}
            {statusCell(location)}
          </div>
        </Link>
      )}
      getItemKey={(location) => location.locationId}
      isFiltered={!!search || !!statusFilter}
      emptyMessage="No locations found"
      emptyState={
        <EmptyState className="m-auto">
          <EmptyState.Icon>
            <MapPin className="size-4" />
          </EmptyState.Icon>
          <EmptyState.Content>
            <EmptyState.Title>No locations yet</EmptyState.Title>
            <EmptyState.Description>Add a location to place devices and book visits in it.</EmptyState.Description>
          </EmptyState.Content>
          {canCreateLocation && (
            <EmptyState.Actions>
              <ActionButton state={{ allowed: true }} variant="outline" size="sm" to="/catalog/locations/new">
                <Plus className="size-4 mr-1" />
                Location
              </ActionButton>
            </EmptyState.Actions>
          )}
        </EmptyState>
      }
    />
  );
}
